import React from 'react';

import { Box, Button, FormField, PageHeader } from 'components';
import { useNotifications } from 'hooks';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useUser } from 'modules/user';

import { userSelectors } from 'selectors';

enum FormFields {
  firstName = 'firstName',
  lastName = 'lastName',
  email = 'email',
}

type FormData = {
  [FormFields.firstName]: string;
  [FormFields.lastName]: string;
  [FormFields.email]: string;
};

type Props = {
  onModalClose: () => void;
};

export const InviteUserForm: React.FC<Props> = ({ onModalClose }) => {
  const { error, success } = useNotifications();
  const { inviteUser } = useUser();
  const { t } = useTranslation();
  const inviteUserPending = useSelector(userSelectors.inviteUserPending);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>();

  const isFormPending = inviteUserPending;

  const handleCancel = () => {
    onModalClose();
  };

  const onSubmit = async (formData: FormData) => {
    const result = await inviteUser(formData);
    if (result.error) {
      error(result.payload.body?.title || t('userForm.edit.error'));
    } else {
      success(t('userForm.edit.sucscefull'));
      onModalClose();
    }
    return;
  };

  return (
    <Box padding="1.5rem">
      <form>
        <PageHeader
          header={t('userFor.invite.header')}
          subHeader={t('userFor.invite.subHeader')}
        >
          <Box display="flex" columnGap="1rem">
            <Button
              type="submit"
              variant="outlined"
              onClick={handleSubmit(onSubmit)}
              disabled={isFormPending}
              size="medium"
              color="primary"
            >
              {t('common.form.invite')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={handleCancel}
              disabled={isFormPending}
              size="medium"
              color="error"
            >
              {t('common.form.cancel')}
            </Button>
          </Box>
        </PageHeader>
        <FormField
          fieldName={FormFields.firstName}
          fieldError={errors[FormFields.firstName] as FieldError}
          label={t('membersList.column.firstName')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.firstName')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending}
        />
        <FormField
          fieldName={FormFields.lastName}
          fieldError={errors[FormFields.lastName] as FieldError}
          label={t('membersList.column.lastName')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.lastName')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending}
        />
        <FormField
          fieldName={FormFields.email}
          fieldError={errors[FormFields.email] as FieldError}
          label={t('membersList.column.email')}
          placeholder={
            t('common.form.enterValueFor') + t('membersList.column.email')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={100}
          isRequired
          isDisabled={isFormPending}
        />
      </form>
    </Box>
  );
};
